import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import type { MessageKey } from '../i18n/messages'
import { DateLisible } from '../components/Nombre'
import { Badge, Button, Card, DataTable, PageHeader, SelectField, Stack, useToast } from '../components/ui'
import type { Column, Tone } from '../components/ui'
import { growthApi } from './growthApi'
import type { Lead, LeadStatus } from './growthApi'

const TONE_PAR_STATUT: Record<LeadStatus, Tone> = {
  new: 'info',
  handled: 'succes',
  spam: 'erreur',
}

/**
 * Les prospects du site vitrine (migration 0083) : ce que le formulaire public écrit dans
 * `saas.lead`, lu ici par `core.platform_list_leads`.
 *
 * SANS CET ÉCRAN, LA TABLE EXISTERAIT SANS LECTEUR — exactement la complétude simulée que
 * le dépôt évite ailleurs. Et sans ses boutons, les statuts « traité » et « indésirable »
 * n'existeraient que dans la contrainte : le filtre rendrait toujours une liste vide. Rien n'est décidé côté client : la fonction de lecture franchit
 * le cloisonnement sans vérifier de rôle, et c'est `ListLeadsHandler` côté serveur qui
 * refuse un rôle client, pas cet écran (voir sa note de sûreté).
 */
export function LeadsPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const token = session?.token ?? ''

  const [filtre, setFiltre] = useState<LeadStatus | ''>('')
  const [prospects, setProspects] = useState<Lead[]>([])

  const charger = useCallback(() => {
    if (token === '') return
    growthApi
      .listLeads(token, filtre === '' ? null : filtre, 100)
      .then(setProspects)
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
  }, [token, filtre, notify, describeError])

  useEffect(charger, [charger])

  const [enCours, setEnCours] = useState<number | null>(null)

  const classer = async (prospect: Lead, statut: LeadStatus) => {
    setEnCours(prospect.id)
    try {
      await growthApi.setLeadStatus(token, prospect.id, statut)
      charger()
    } catch (cause) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnCours(null)
    }
  }

  const colonnes: Column<Lead>[] = [
    { key: 'date', header: t('growth.leads.date'), render: (p) => <DateLisible valeur={p.createdAt} /> },
    { key: 'name', header: t('growth.leads.name'), render: (p) => p.fullName },
    { key: 'company', header: t('growth.leads.company'), render: (p) => p.companyName ?? '—', secondary: true },
    { key: 'email', header: t('growth.leads.email'), render: (p) => p.email },
    { key: 'phone', header: t('growth.leads.phone'), render: (p) => p.phone ?? '—', secondary: true },
    { key: 'governorate', header: t('growth.leads.governorate'), render: (p) => p.governorate ?? '—', secondary: true },
    { key: 'source', header: t('growth.leads.source'), render: (p) => p.source, secondary: true },
    {
      key: 'status',
      header: t('growth.leads.status'),
      render: (p) => <Badge tone={TONE_PAR_STATUT[p.status]}>{t(`growth.leadStatus.${p.status}` as MessageKey)}</Badge>,
    },
    { key: 'message', header: t('growth.leads.message'), render: (p) => p.message },
    {
      key: 'action',
      header: t('growth.leads.action'),
      render: (p) =>
        p.status === 'new' ? (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button variant="secondaire" pending={enCours === p.id} onClick={() => void classer(p, 'handled')}>
              {t('growth.leads.markHandled')}
            </Button>
            <Button variant="danger" pending={enCours === p.id} onClick={() => void classer(p, 'spam')}>
              {t('growth.leads.markSpam')}
            </Button>
          </div>
        ) : (
          <Button variant="secondaire" pending={enCours === p.id} onClick={() => void classer(p, 'new')}>
            {t('growth.leads.reopen')}
          </Button>
        ),
    },
  ]

  return (
    <Stack>
      <PageHeader title={t('growth.leads.title')} description={t('growth.leads.description')} />
      <Card title={t('growth.leads.list')}>
        <Stack gap={3}>
          <div style={{ maxWidth: '16rem' }}>
            <SelectField
              label={t('growth.leads.status')}
              value={filtre}
              onChange={(v) => setFiltre(v as LeadStatus | '')}
              options={[
                { value: '', label: t('billing.status.all') },
                { value: 'new', label: t('growth.leadStatus.new') },
                { value: 'handled', label: t('growth.leadStatus.handled') },
                { value: 'spam', label: t('growth.leadStatus.spam') },
              ]}
            />
          </div>
          <DataTable columns={colonnes} rows={prospects} rowKey={(p) => p.id} emptyLabel={t('growth.leads.empty')} />
        </Stack>
      </Card>
    </Stack>
  )
}
